import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FeaturedIcon, SpinnerIcon } from '@/lib';
import { BaseModal } from '@/lib/Modals';
import { SuggestedAudience } from '../types';
import { SuggestedAudienceCreatePhase } from '../hooks/useSuggestedAudienceCreate';

type SuggestedAudienceCreateOverlaysProps = {
  phase: SuggestedAudienceCreatePhase;
  activeAudience: SuggestedAudience | null;
  missingDatasourceName: string | null;
  isEnablingConsent: boolean;
  onCancelGeneration: () => void;
  onCloseModals: () => void;
  onEnableConsent: () => void;
  onRegenerate: () => void;
};

const LockIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width='20'
    height='20'
    viewBox='0 0 20 20'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.6'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden
  >
    <rect x='4' y='9' width='12' height='8' rx='1.5' />
    <path d='M7 9V6.5a3 3 0 0 1 6 0V9' />
  </svg>
);

const WarningIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width='22'
    height='22'
    viewBox='0 0 20 20'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.7'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden
  >
    <path d='M10 3 2.5 16h15L10 3Z' />
    <path d='M10 8.5v3.5M10 14.5v.2' />
  </svg>
);

export const SuggestedAudienceCreateOverlays: FC<SuggestedAudienceCreateOverlaysProps> = ({
  phase,
  activeAudience,
  missingDatasourceName,
  isEnablingConsent,
  onCancelGeneration,
  onCloseModals,
  onEnableConsent,
  onRegenerate,
}) => {
  const { t } = useTranslation('marketIntelligence');

  if (phase === 'generating') {
    return (
      <BaseModal
        onClose={onCancelGeneration}
        isDismissable={false}
        classNames={{
          dialog: 'max-w-[500px]',
          content: 'min-h-0 items-center text-center justify-center gap-3.5 py-10',
        }}
      >
        <SpinnerIcon className='size-10 text-brand-600' />
        <h2 className='mt-1.5 text-xl font-extrabold text-primary-900'>{t('report.suggestedAudiences.create.generating.title')}</h2>
        {activeAudience?.title ? <p className='text-sm text-primary-700'>“{activeAudience.title}”</p> : null}
        <p className='max-w-sm text-xs leading-5 text-primary-500'>{t('report.suggestedAudiences.create.generating.hint')}</p>
        <Button intent='secondary' size='md' className='mt-2.5' onClick={onCancelGeneration}>
          {t('report.suggestedAudiences.create.generating.cancel')}
        </Button>
      </BaseModal>
    );
  }

  if (phase === 'modalDs') {
    return (
      <BaseModal
        onClose={onCloseModals}
        classNames={{
          dialog: 'max-w-[480px]',
          content: 'min-h-0 items-center text-center justify-center gap-3.5 py-8',
        }}
      >
        <FeaturedIcon color='warning' size='lg' type='light'>
          <WarningIcon className='size-6 text-orange-600' />
        </FeaturedIcon>
        <h2 className='text-xl font-extrabold text-primary-900'>{t('report.suggestedAudiences.create.missingDatasource.title')}</h2>
        <p className='text-sm leading-6 text-primary-700'>
          {t('report.suggestedAudiences.create.missingDatasource.description', {
            name: missingDatasourceName || t('report.suggestedAudiences.create.missingDatasource.fallbackName'),
          })}
        </p>
        <div className='mt-3 flex flex-wrap justify-center gap-3.5'>
          <Button intent='secondary' size='md' onClick={onCloseModals}>
            {t('report.suggestedAudiences.create.missingDatasource.close')}
          </Button>
          <Button
            intent='primary'
            size='md'
            onClick={() => {
              onCloseModals();
              void onRegenerate();
            }}
          >
            {t('report.suggestedAudiences.create.missingDatasource.regenerate')}
          </Button>
        </div>
      </BaseModal>
    );
  }

  if (phase === 'modal403') {
    return (
      <BaseModal
        onClose={onCloseModals}
        classNames={{
          dialog: 'max-w-[480px]',
          content: 'min-h-0 items-center text-center justify-center gap-3.5 py-8',
        }}
      >
        <FeaturedIcon color='brand' size='lg' type='light'>
          <LockIcon className='size-5 text-brand-700' />
        </FeaturedIcon>
        <h2 className='text-xl font-extrabold text-primary-900'>{t('report.suggestedAudiences.create.consentRevoked.title')}</h2>
        <p className='text-sm leading-6 text-primary-700'>{t('report.suggestedAudiences.create.consentRevoked.description')}</p>
        <div className='mt-3 flex flex-wrap justify-center gap-3.5'>
          <Button intent='secondary' size='md' onClick={onCloseModals} disabled={isEnablingConsent}>
            {t('report.suggestedAudiences.create.consentRevoked.dismiss')}
          </Button>
          <Button intent='primary' size='md' onClick={onEnableConsent} isLoading={isEnablingConsent}>
            {t('report.suggestedAudiences.create.consentRevoked.enable')}
          </Button>
        </div>
      </BaseModal>
    );
  }

  return null;
};
